import { useState, useEffect, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { fetchAcademicSessions, createAcademicSession, updateAcademicSession, type AcademicSessionRecord } from '../../services/examApi';
import type { ApiError } from '../../services/api';
import './AcademicSessions.css';

/* ── Types ─────────────────────────────────────────── */

interface FormErrors {
    name?: string;
    start_date?: string;
    end_date?: string;
}

/* ── Helpers ───────────────────────────────────────── */

function formatDate(iso: string): string {
    const d = new Date(iso + 'T00:00:00');
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

/* ── Component ─────────────────────────────────────── */

type ViewMode = 'list' | 'create' | 'edit';

export default function AcademicSessions() {
    const { t } = useTranslation();
    /* --- data state --- */
    const [sessions, setSessions] = useState<AcademicSessionRecord[]>([]);
    const [editingId, setEditingId] = useState<string | null>(null);

    /* --- view state --- */
    const [view, setView] = useState<ViewMode>('list');
    const [successMsg, setSuccessMsg] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [fetchError, setFetchError] = useState('');

    /* --- fetch sessions on mount --- */
    useEffect(() => {
        (async () => {
            try {
                setIsLoading(true);
                setFetchError('');
                const data = await fetchAcademicSessions();
                setSessions(data);
            } catch (err: unknown) {
                const message = err && typeof err === 'object' && 'message' in err
                    ? (err as { message: string }).message
                    : t('sessions.fetchError');
                setFetchError(message);
            } finally {
                setIsLoading(false);
            }
        })();
    }, [t]);

    /* --- form state --- */
    const [name, setName] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [isCurrent, setIsCurrent] = useState(false);

    /* --- UI state --- */
    const [errors, setErrors] = useState<FormErrors>({});
    const [isSubmitting, setIsSubmitting] = useState(false);

    /* ---- Navigation ---- */
    const goToCreate = () => {
        resetForm();
        setView('create');
    };

    const goToEdit = (session: AcademicSessionRecord) => {
        setName(session.name);
        setStartDate(session.start_date);
        setEndDate(session.end_date);
        setIsCurrent(session.is_current);
        setEditingId(session.id);
        setErrors({});
        setView('edit');
    };

    const goToList = () => {
        resetForm();
        setView('list');
    };

    const resetForm = () => {
        setName('');
        setStartDate('');
        setEndDate('');
        setIsCurrent(false);
        setEditingId(null);
        setErrors({});
    };

    /* ---- Validation ---- */
    const validate = (): boolean => {
        const newErrors: FormErrors = {};

        if (!name.trim()) {
            newErrors.name = t('sessions.nameRequired');
        } else if (name.trim().length > 20) {
            newErrors.name = t('sessions.nameMaxLength');
        } else {
            // Check uniqueness (exclude current editing session)
            const duplicate = sessions.find(
                (s) => s.name.toLowerCase() === name.trim().toLowerCase() && s.id !== editingId
            );
            if (duplicate) {
                newErrors.name = t('sessions.nameDuplicate');
            }
        }

        if (!startDate) {
            newErrors.start_date = t('sessions.startDateRequired');
        }

        if (!endDate) {
            newErrors.end_date = t('sessions.endDateRequired');
        } else if (startDate && endDate <= startDate) {
            newErrors.end_date = t('sessions.endDateAfterStart');
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    /* ---- Submit ---- */
    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();
        if (!validate()) return;

        setIsSubmitting(true);

        const payload = {
            name: name.trim(),
            start_date: startDate,
            end_date: endDate,
            is_current: isCurrent || undefined,
        };

        try {
            if (view === 'edit' && editingId) {
                await updateAcademicSession(editingId, payload);
                const data = await fetchAcademicSessions();
                setSessions(data);
                setSuccessMsg(t('sessions.updatedSuccess'));
            } else {
                await createAcademicSession(payload);
                const data = await fetchAcademicSessions();
                setSessions(data);
                setSuccessMsg(t('sessions.createdSuccess'));
            }

            resetForm();
            setView('list');
            setTimeout(() => setSuccessMsg(''), 5000);
        } catch (err: unknown) {
            const apiErr = err as ApiError;
            if (apiErr.errors) {
                const serverErrors: FormErrors = {};
                if (apiErr.errors.name) serverErrors.name = apiErr.errors.name[0];
                if (apiErr.errors.start_date) serverErrors.start_date = apiErr.errors.start_date[0];
                if (apiErr.errors.end_date) serverErrors.end_date = apiErr.errors.end_date[0];
                setErrors(serverErrors);
            } else {
                setErrors({ name: apiErr.message || t('sessions.createError') });
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    /* ────────────────── Render: List View ────────────────── */
    if (view === 'list') {
        return (
            <div className="acad-sessions">
                {/* Success toast */}
                {successMsg && (
                    <div className="acad-sessions__toast">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                            <polyline points="22 4 12 14.01 9 11.01" />
                        </svg>
                        <span>{successMsg}</span>
                        <button className="acad-sessions__toast-dismiss" onClick={() => setSuccessMsg('')} aria-label="Dismiss">✕</button>
                    </div>
                )}

                <div className="acad-sessions__header">
                    <div className="acad-sessions__header-text">
                        <h1 className="acad-sessions__title">{t('sessions.title')}</h1>
                        <p className="acad-sessions__subtitle">
                            {t('sessions.subtitle')}
                        </p>
                    </div>
                    <button className="acad-sessions__add-btn" onClick={goToCreate}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="12" y1="5" x2="12" y2="19" />
                            <line x1="5" y1="12" x2="19" y2="12" />
                        </svg>
                        {t('sessions.addSession')}
                    </button>
                </div>

                {fetchError && (
                    <div className="acad-sessions__error-banner">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="12" cy="12" r="10" />
                            <line x1="12" y1="8" x2="12" y2="12" />
                            <line x1="12" y1="16" x2="12.01" y2="16" />
                        </svg>
                        {fetchError}
                    </div>
                )}

                <div className="acad-sessions__table-wrap">
                    {isLoading ? (
                        <div className="acad-sessions__loading">
                            <svg className="acad-sessions__spinner" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                <circle cx="12" cy="12" r="10" strokeWidth="3" />
                            </svg>
                            <span>{t('common.loading')}</span>
                        </div>
                    ) : sessions.length === 0 ? (
                        <div className="acad-sessions__empty">
                            <div className="acad-sessions__empty-icon">
                                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                                    <line x1="16" y1="2" x2="16" y2="6" />
                                    <line x1="8" y1="2" x2="8" y2="6" />
                                    <line x1="3" y1="10" x2="21" y2="10" />
                                </svg>
                            </div>
                            <h3 className="acad-sessions__empty-title">{t('sessions.noSessionsYet')}</h3>
                            <p className="acad-sessions__empty-text">
                                {t('sessions.createFirstSession')}
                            </p>
                        </div>
                    ) : (
                        <table className="acad-sessions__table">
                            <thead>
                                <tr>
                                    <th>{t('sessions.sessionName')}</th>
                                    <th>{t('sessions.startDate')}</th>
                                    <th>{t('sessions.endDate')}</th>
                                    <th>{t('common.current')}</th>
                                    <th className="acad-sessions__col-actions">{t('common.actions')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {sessions.map((s) => (
                                    <tr key={s.id}>
                                        <td className="acad-sessions__col-name">{s.name}</td>
                                        <td>{formatDate(s.start_date)}</td>
                                        <td>{formatDate(s.end_date)}</td>
                                        <td>
                                            {s.is_current ? (
                                                <span className="acad-sessions__badge acad-sessions__badge--current">
                                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                                        <polyline points="20 6 9 17 4 12" />
                                                    </svg>
                                                    {t('common.current')}
                                                </span>
                                            ) : (
                                                <span style={{ color: '#94a3b8', fontSize: '0.8125rem' }}>—</span>
                                            )}
                                        </td>
                                        <td className="acad-sessions__col-actions">
                                            <button
                                                className="acad-sessions__edit-btn"
                                                onClick={() => goToEdit(s)}
                                            >
                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                                                </svg>
                                                {t('common.edit')}
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
        );
    }

    /* ────────────────── Render: Create / Edit Form ────────────────── */
    return (
        <div className="acad-sessions">
            <div className="acad-sessions__form-header">
                <button className="acad-sessions__back-btn" onClick={goToList} title={t('sessions.backToList')}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="15 18 9 12 15 6" />
                    </svg>
                </button>
                <h1 className="acad-sessions__form-title">
                    {view === 'edit' ? t('sessions.editSession') : t('sessions.createSession')}
                </h1>
            </div>

            <div className="acad-sessions__card">
                <form className="acad-sessions__form" onSubmit={handleSubmit} noValidate>

                    {/* Session Name */}
                    <div className="acad-sessions__field">
                        <label className="acad-sessions__label acad-sessions__label--required" htmlFor="sessionName">
                            {t('sessions.sessionName')}
                        </label>
                        <div className="acad-sessions__input-wrap">
                            <span className="acad-sessions__input-icon">
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M4 7V4h16v3" />
                                    <path d="M9 20h6" />
                                    <path d="M12 4v16" />
                                </svg>
                            </span>
                            <input
                                className={`acad-sessions__input ${errors.name ? 'acad-sessions__input--error' : ''}`}
                                type="text"
                                id="sessionName"
                                placeholder="e.g. 2025-26"
                                value={name}
                                onChange={(e) => {
                                    setName(e.target.value);
                                    if (errors.name) setErrors((prev) => ({ ...prev, name: undefined }));
                                }}
                                maxLength={20}
                                autoFocus
                            />
                        </div>
                        {errors.name ? (
                            <p className="acad-sessions__error">{errors.name}</p>
                        ) : (
                            <p className="acad-sessions__helper">{t('sessions.nameHelper')}</p>
                        )}
                    </div>

                    {/* Start Date & End Date */}
                    <div className="acad-sessions__row">
                        <div className="acad-sessions__field">
                            <label className="acad-sessions__label acad-sessions__label--required" htmlFor="startDate">
                                {t('sessions.startDate')}
                            </label>
                            <input
                                className={`acad-sessions__input acad-sessions__input--date ${errors.start_date ? 'acad-sessions__input--error' : ''}`}
                                type="date"
                                id="startDate"
                                value={startDate}
                                onChange={(e) => {
                                    setStartDate(e.target.value);
                                    if (errors.start_date) setErrors((prev) => ({ ...prev, start_date: undefined }));
                                    // If end-date is now invalid, clear it
                                    if (endDate && e.target.value && endDate <= e.target.value) {
                                        setEndDate('');
                                    }
                                }}
                            />
                            {errors.start_date && <p className="acad-sessions__error">{errors.start_date}</p>}
                        </div>

                        <div className="acad-sessions__field">
                            <label className="acad-sessions__label acad-sessions__label--required" htmlFor="endDate">
                                {t('sessions.endDate')}
                            </label>
                            <input
                                className={`acad-sessions__input acad-sessions__input--date ${errors.end_date ? 'acad-sessions__input--error' : ''}`}
                                type="date"
                                id="endDate"
                                value={endDate}
                                min={startDate ? (() => {
                                    const d = new Date(startDate + 'T00:00:00');
                                    d.setDate(d.getDate() + 1);
                                    return d.toISOString().split('T')[0];
                                })() : undefined}
                                onChange={(e) => {
                                    setEndDate(e.target.value);
                                    if (errors.end_date) setErrors((prev) => ({ ...prev, end_date: undefined }));
                                }}
                            />
                            {errors.end_date && <p className="acad-sessions__error">{errors.end_date}</p>}
                        </div>
                    </div>

                    <hr className="acad-sessions__divider" />

                    {/* Set as Current */}
                    <div className="acad-sessions__field">
                        <div className="acad-sessions__toggle-row">
                            <div className="acad-sessions__toggle-info">
                                <span className="acad-sessions__toggle-label">{t('sessions.setAsCurrent')}</span>
                                <span className="acad-sessions__toggle-desc">{t('sessions.setAsCurrentDesc')}</span>
                            </div>
                            <label className="acad-sessions__toggle">
                                <input
                                    type="checkbox"
                                    checked={isCurrent}
                                    onChange={(e) => setIsCurrent(e.target.checked)}
                                />
                                <span className="acad-sessions__toggle-track" />
                            </label>
                        </div>
                        {isCurrent && (
                            <div className="acad-sessions__warning">
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                                    <line x1="12" y1="9" x2="12" y2="13" />
                                    <line x1="12" y1="17" x2="12.01" y2="17" />
                                </svg>
                                {t('sessions.currentWarning')}
                            </div>
                        )}
                    </div>

                    <hr className="acad-sessions__divider" />

                    {/* Actions */}
                    <div className="acad-sessions__actions">
                        <button
                            className="acad-sessions__submit"
                            type="submit"
                            disabled={isSubmitting}
                        >
                            {isSubmitting ? (
                                <>
                                    <svg className="acad-sessions__spinner" width="18" height="18" viewBox="0 0 24 24" fill="none">
                                        <circle cx="12" cy="12" r="10" strokeWidth="3" />
                                    </svg>
                                    {view === 'edit' ? t('common.updating') : t('common.creating')}
                                </>
                            ) : (
                                view === 'edit' ? t('sessions.updateSession') : t('sessions.createSession')
                            )}
                        </button>
                        <button
                            className="acad-sessions__cancel"
                            type="button"
                            onClick={goToList}
                            disabled={isSubmitting}
                        >
                            {t('common.cancel')}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
